import React, { useEffect, useState, useCallback, useMemo } from "react";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError, formatIDR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Copy } from "lucide-react";
import GiftCardOverlayEditor, { buildDefaultOverlays, GiftCardOverlayPreview } from "./GiftCardOverlayEditor";

/* eslint-disable no-unused-vars */
// Kept for legacy templates that may still reference the butterfly tint
// on the row thumbnail even though the editor no longer surfaces it.
const LOGO_TINT_SWATCHES_LEGACY = null;
/* eslint-enable no-unused-vars */

// Preset palette for the butterfly silhouette that fills the left edge of
// every legacy gift card. Retained so previously-saved templates that
// reference these swatches still round-trip through the row-preview
// component.
const LOGO_TINT_SWATCHES = [
  { value: "#C79A5B", label: "Cocoon Gold" },
  { value: "#728971", label: "Sage" },
  { value: "#2C2D28", label: "Charcoal" },
  { value: "#8B5E3C", label: "Bronze" },
  { value: "#B45B7C", label: "Rose" },
  { value: "#4B6E8C", label: "Slate Blue" },
  { value: "#7A6BA0", label: "Amethyst" },
  { value: "#D06B4A", label: "Terracotta" },
  { value: "#E7B84A", label: "Champagne" },
  { value: "#5A8B76", label: "Emerald" },
];

const emptyTemplate = {
  title: "Cocoon Gift Card",
  tagline: "",
  message: "Redeemable for any service at Cocoon Medical Spa.",
  accent_color: "#728971",
  background_color: "#F9F8F6",
  logo_tint: "#C79A5B",
  terms: "Non-refundable. Valid at all Cocoon Medical Spa locations. One-time use.",
  branch_ids: [],
  active: true,
  background_image_data_url: "",
  logo_image_data_url: "",
  background_scale: 100,
  background_position: "center",
  overlays: buildDefaultOverlays(),
};

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active (unused)" },
  { value: "half_redeemed", label: "Half redeemed" },
  { value: "used", label: "Used" },
  { value: "expired", label: "Expired" },
  { value: "void", label: "Void" },
];

export default function BrandGiftCertificates() {
  const [tab, setTab] = useState("templates");
  const [branches, setBranches] = useState([]);
  const [templates, setTemplates] = useState([]);

  const loadTemplates = useCallback(async () => {
    try {
      const [b, t] = await Promise.all([
        staffApi.get("/branches"),
        staffApi.get("/gift-card-templates"),
      ]);
      setBranches(b.data || []);
      setTemplates(t.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  return (
    <StaffShell context="brand">
      <div className="max-w-6xl">
        <p className="tracking-overline text-xs text-muted-foreground">BRAND SETTINGS</p>
        <h1 className="text-3xl font-semibold mt-1 mb-1" data-testid="brand-giftcerts-title">Gift Card</h1>
        <p className="text-sm text-muted-foreground mb-6">Design a Cocoon-branded gift card template once — it becomes available at every branch you tick, and the value + validity are captured at each POS sale.</p>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-transparent p-0 border-b border-border rounded-none w-full justify-start gap-1 h-auto mb-6">
            <TabsTrigger value="templates" data-testid="tab-templates" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2">Templates</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2">History</TabsTrigger>
          </TabsList>

          <TabsContent value="templates" className="mt-0">
            <TemplatesTab branches={branches} templates={templates} reload={loadTemplates} />
          </TabsContent>

          <TabsContent value="history" className="mt-0">
            <HistoryTab branches={branches} />
          </TabsContent>
        </Tabs>
      </div>
    </StaffShell>
  );
}

/* ─────────────────────────── Templates tab ─────────────────────────── */

function TemplatesTab({ branches, templates, reload }) {
  const { staff } = useAuth();
  // Uniform CRUD gates driven by the role's Gift Card permission
  // ticks. Owners always resolve to `true` inside `hasPermission`, so
  // the Owner login keeps full control regardless of the tick state.
  // Any future role the operator invents inherits the same rules
  // automatically — no per-role hard-coding needed.
  const canCreate = hasPermission(staff, "brand.gift_card.create");
  const canEdit = hasPermission(staff, "brand.gift_card.edit");
  const canDelete = hasPermission(staff, "brand.gift_card.delete");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTemplate);

  const branchNameById = useMemo(() => {
    const m = new Map();
    branches.forEach((b) => m.set(b.id, b.name));
    return m;
  }, [branches]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyTemplate });
    setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      ...emptyTemplate,
      ...t,
      branch_ids: Array.isArray(t.branch_ids) ? t.branch_ids : [],
      logo_tint: t.logo_tint || "#C79A5B",
      // Merge saved overlays over defaults so legacy templates
      // (missing the overlays field) still get a sensible layout.
      overlays: { ...buildDefaultOverlays(), ...(t.overlays || {}) },
    });
    setOpen(true);
  };

  /**
   * One-click duplicate — POSTs an exact clone of `t` to the brand
   * template collection (X/Y coords, sizes, align toggles, bounding
   * box widths, uploaded background + logo data URLs, branch scoping
   * — every field the editor cares about). The name gets ` - Copy`
   * appended. On success we open the returned server-side row in the
   * editor so the operator can tweak the background/name straight
   * away without a second click.
   */
  const duplicate = async (t) => {
    try {
      // structuredClone keeps deeply-nested overlay objects intact;
      // falls back to a JSON round-trip on very old browsers.
      const deep = (typeof structuredClone === "function")
        ? structuredClone(t)
        : JSON.parse(JSON.stringify(t));
      // Strip server-managed keys so the POST endpoint mints fresh ones.
      delete deep.id;
      delete deep.created_at;
      delete deep.updated_at;
      const payload = {
        ...emptyTemplate,
        ...deep,
        title: `${(t.title || "Gift Card").trim()} - Copy`,
        branch_ids: Array.isArray(deep.branch_ids) ? deep.branch_ids : [],
        overlays: { ...buildDefaultOverlays(), ...(deep.overlays || {}) },
        // New copy always starts active so the operator sees it in the
        // POS as soon as they hit Save.
        active: true,
      };
      const r = await staffApi.post(`/gift-card-templates`, payload);
      toast.success("Template duplicated — opening for edit");
      await reload();
      // Drop straight into the editor so the operator can immediately
      // swap the background image or rename before saving again.
      openEdit(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    try {
      const payload = {
        title: form.title,
        tagline: form.tagline || "",
        message: form.message || "",
        accent_color: form.accent_color,
        background_color: form.background_color,
        logo_tint: form.logo_tint,
        terms: form.terms || "",
        branch_ids: form.branch_ids || [],
        active: !!form.active,
        background_image_data_url: form.background_image_data_url || "",
        logo_image_data_url: form.logo_image_data_url || "",
        background_scale: Number(form.background_scale) || 100,
        background_position: form.background_position || "center",
        overlays: form.overlays || buildDefaultOverlays(),
      };
      if (editing) await staffApi.put(`/gift-card-templates/${editing.id}`, payload);
      else await staffApi.post(`/gift-card-templates`, payload);
      toast.success("Template saved");
      setOpen(false);
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}"? This affects all branches.`)) return;
    try { await staffApi.delete(`/gift-card-templates/${t.id}`); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (t, next) => {
    try {
      await staffApi.patch(`/gift-card-templates/${t.id}`, { active: next });
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const describeBranches = (t) => {
    const ids = Array.isArray(t.branch_ids) ? t.branch_ids : [];
    if (ids.length === 0) return "All branches";
    return ids.map((id) => branchNameById.get(id) || "—").join(", ");
  };

  return (
    <>
      <div className="flex items-end justify-between mb-4">
        <p className="text-sm text-muted-foreground">Each template is a reusable gift-certificate design. Toggle a row on/off to control whether it appears in branch POS.</p>
        {canCreate && (
          <Button onClick={openNew} data-testid="new-gift-template-btn" className="gap-1"><Plus className="w-4 h-4" /> New template</Button>
        )}
      </div>

      <Card className="rounded-xl">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Name</TableHead>
                <TableHead>Available at</TableHead>
                <TableHead className="text-center w-24">Active</TableHead>
                <TableHead className="w-40 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-10">No templates yet — design your first gift card.</TableCell></TableRow>
              ) : templates.map((t) => (
                <TableRow key={t.id} data-testid={`gift-template-row-${t.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <TemplateRowPreview t={t} />
                      <div>
                        <div className="font-medium text-sm" data-testid={`gift-template-name-${t.id}`}>{t.title}</div>
                        {t.message && <div className="text-xs text-muted-foreground italic line-clamp-1">{t.message}</div>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{describeBranches(t)}</TableCell>
                  <TableCell className="text-center">
                    <Switch
                      checked={t.active !== false}
                      onCheckedChange={(v) => toggleActive(t, v)}
                      data-testid={`gift-template-active-${t.id}`}
                      disabled={!canEdit}
                    />
                  </TableCell>
                  <TableCell className="w-40 text-right whitespace-nowrap align-middle">
                    <div className="inline-flex items-center gap-3 justify-end" data-testid={`gift-template-actions-${t.id}`}>
                      {canEdit && (
                        <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={() => openEdit(t)} data-testid={`gift-template-edit-${t.id}`} title="Edit template"><Pencil className="w-4 h-4" /></Button>
                      )}
                      {/* Duplicate is a create-shape action — same gate
                          as "New template" so a role without Create
                          can't clone-around the block. */}
                      {canCreate && (
                        <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={() => duplicate(t)} data-testid={`gift-template-duplicate-${t.id}`} title="Duplicate template"><Copy className="w-4 h-4" /></Button>
                      )}
                      {canDelete && (
                        <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={() => remove(t)} data-testid={`gift-template-delete-${t.id}`} title="Delete template"><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <TemplateEditor
        open={open}
        setOpen={setOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        branches={branches}
        onSave={save}
      />
    </>
  );
}

/* ─────────────────────── Template editor dialog ─────────────────────── */

function TemplateEditor({ open, setOpen, editing, form, setForm, branches, onSave }) {
  const allBranches = form.branch_ids.length === 0;
  const toggleBranch = (bid, checked) => {
    setForm((f) => {
      const set = new Set(f.branch_ids || []);
      if (checked) set.add(bid); else set.delete(bid);
      return { ...f, branch_ids: Array.from(set) };
    });
  };
  const setAllBranches = () => setForm((f) => ({ ...f, branch_ids: [] }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="gift-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit template" : "New template"}</DialogTitle>
        </DialogHeader>

        {/* Custom Overlay editor — replaces the old fixed layout. */}
        <GiftCardOverlayEditor form={form} setForm={setForm} />

        {/* Terms + branch scoping stay on the outer editor because they
            aren't part of the visual overlay. */}
        <div className="grid md:grid-cols-2 gap-6 mt-4 pt-4 border-t border-border">
          <div>
            <Label>Terms</Label>
            <Textarea rows={3} value={form.terms || ""} onChange={(e) => setForm({ ...form, terms: e.target.value })} data-testid="gc-terms" />
          </div>
          <div>
            <Label>Available at branches</Label>
            <div className="mt-2 space-y-2 border border-border rounded-md p-3 bg-muted/30 max-h-52 overflow-y-auto">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={allBranches} onCheckedChange={(v) => v && setAllBranches()} data-testid="gc-branch-all" />
                <span className="text-sm font-medium">All branches</span>
              </label>
              <div className="pl-6 space-y-1.5 border-l border-border ml-2">
                {branches.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={form.branch_ids.includes(b.id)}
                      onCheckedChange={(v) => toggleBranch(b.id, !!v)}
                      data-testid={`gc-branch-${b.id}`}
                    />
                    <span className="text-sm">{b.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground pt-1">Tick specific branches to restrict this template, or leave every branch box empty to make it available everywhere.</p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={onSave} data-testid="save-gift-btn">Save template</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ────────────────────────── Preview thumbnail ────────────────────────── */

function TemplateRowPreview({ t }) {
  if (t.background_image_data_url || (t.overlays && Object.keys(t.overlays).length)) {
    return (
      <div className="w-16 h-10 rounded-md border border-border overflow-hidden shrink-0 bg-muted/40">
        <GiftCardOverlayPreview data={t} testidPrefix={`gift-preview-thumb-${t.id || "row"}`} />
      </div>
    );
  }
  return (
    <div
      className="w-10 h-10 rounded-md border border-border overflow-hidden shrink-0 relative"
      style={{ background: t.background_color || "#F9F8F6" }}
    >
      <div
        className="absolute inset-y-0 left-0 w-6"
        style={{
          WebkitMaskImage: "url(/brand/cocoon-butterfly.png)",
          maskImage: "url(/brand/cocoon-butterfly.png)",
          WebkitMaskSize: "cover",
          maskSize: "cover",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskPosition: "left center",
          maskPosition: "left center",
          backgroundColor: t.logo_tint || "#C79A5B",
        }}
      />
    </div>
  );
}

/* ─────────────────────────── History tab ─────────────────────────── */

function HistoryTab({ branches }) {
  const [filters, setFilters] = useState({
    code: "",
    customer_name: "",
    from: "",
    to: "",
    status: "all",
    branch_id: "all",
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.code) params.code = filters.code;
      if (filters.customer_name) params.customer_name = filters.customer_name;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.status && filters.status !== "all") params.status = filters.status;
      if (filters.branch_id && filters.branch_id !== "all") params.branch_id = filters.branch_id;
      const r = await staffApi.get("/gift-cards", { params });
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { search(); }, [search]);

  const reset = () => setFilters({ code: "", customer_name: "", from: "", to: "", status: "all", branch_id: "all" });

  const statusBadge = (c) => {
    const s = c.effective_status;
    const map = {
      active:        "bg-emerald-50 text-emerald-700 border-emerald-200",
      half_redeemed: "bg-amber-50 text-amber-700 border-amber-200",
      used:          "bg-zinc-100 text-zinc-600 border-zinc-200",
      expired:       "bg-rose-50 text-rose-700 border-rose-200",
      void:          "bg-zinc-200 text-zinc-700 border-zinc-300",
    };
    const label = { active: "Active", half_redeemed: "Half redeemed", used: "Used", expired: "Expired", void: "Void" }[s] || s;
    return <Badge variant="outline" className={`rounded-full text-[10px] ${map[s] || ""}`}>{label}</Badge>;
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  return (
    <div>
      <Card className="rounded-xl mb-4">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Gift Card #</Label>
              <Input value={filters.code} onChange={(e) => setFilters({ ...filters, code: e.target.value })} data-testid="filter-code" placeholder="GC-XXXX-XXXX" />
            </div>
            <div>
              <Label className="text-xs">Customer name</Label>
              <Input value={filters.customer_name} onChange={(e) => setFilters({ ...filters, customer_name: e.target.value })} data-testid="filter-customer" />
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} data-testid="filter-from" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} data-testid="filter-to" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Location</Label>
              <Select value={filters.branch_id} onValueChange={(v) => setFilters({ ...filters, branch_id: v })}>
                <SelectTrigger data-testid="filter-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any location</SelectItem>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex items-end justify-end gap-2">
              <Button variant="outline" onClick={reset} data-testid="filter-reset">Reset</Button>
              <Button onClick={search} data-testid="filter-search">Search</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Voucher #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Issued</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">Searching…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">No vouchers match those filters.</TableCell></TableRow>
              ) : rows.map((c) => (
                <TableRow key={c.id} data-testid={`history-row-${c.id}`}>
                  <TableCell className="font-mono text-sm">{c.code}</TableCell>
                  <TableCell className="text-sm">{c.customer_name || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{branchName(c.branch_id)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{(c.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{(c.expires_at || "").slice(0, 10) || "—"}</TableCell>
                  <TableCell className="text-right text-sm">{formatIDR(c.initial_value)}</TableCell>
                  <TableCell className="text-right text-sm font-medium">{formatIDR(c.balance)}</TableCell>
                  <TableCell>{statusBadge(c)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
